import { useState, useEffect } from 'react';
import { API, formatDateTime } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { UserPlus, Key, Clock, Pencil, Trash2 } from 'lucide-react';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [otpRequests, setOtpRequests] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', role: 'stock_manager', mobile: '', username: '' });
  const [editForm, setEditForm] = useState({ name: '', role: '', mobile: '', username: '' });

  const fetchData = async () => {
    try {
      const [uRes, oRes] = await Promise.all([
        API.get('/users'),
        API.get('/auth/otp-requests'),
      ]);
      setUsers(uRes.data);
      setOtpRequests(oRes.data);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 10000); return () => clearInterval(i); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username || form.username.trim().length < 2) { toast.error('Username required (2+ chars)'); return; }
    try {
      await API.post('/users', { ...form, username: form.username.trim().toLowerCase() });
      toast.success(`User "${form.name}" created`);
      setShowCreate(false);
      setForm({ name: '', role: 'stock_manager', mobile: '', username: '' });
      fetchData();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    try {
      await API.put(`/users/${editUser.id}`, editForm);
      toast.success('User updated');
      setEditUser(null);
      fetchData();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleUser = async (userId) => {
    try {
      const res = await API.put(`/users/${userId}/toggle`);
      toast.success(res.data.is_active ? 'Activated' : 'Deactivated');
      fetchData();
    } catch(err) { toast.error('Failed'); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, role: u.role, mobile: u.mobile || '', username: u.username || '' });
  };

  const roleColors = {
    admin: 'bg-purple-950/40 text-purple-400',
    analyst: 'bg-cyan-950/40 text-cyan-400',
    store_manager: 'bg-blue-950/40 text-blue-400',
    stock_manager: 'bg-emerald-950/40 text-emerald-400',
    billing_executive: 'bg-amber-950/40 text-amber-400',
  };

  return (
    <div data-testid="users-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div><h1>Users</h1><p className="hidden sm:block">Manage staff accounts & OTP requests</p></div>
        <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto" data-testid="user-create-button">
          <UserPlus size={16} className="mr-2" />New User
        </Button>
      </div>

      {/* OTP Requests */}
      {otpRequests.length > 0 && (
        <Card className="mb-4" style={{ background: 'hsl(43, 50%, 14%)', borderColor: 'hsl(43, 50%, 25%)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ color: 'hsl(43, 80%, 65%)' }}><Key size={16} /> Active OTP Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {otpRequests.map(otp => (
                <div key={otp.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border" style={{ background: 'hsl(30, 8%, 11%)', borderColor: 'hsl(43, 50%, 25%)' }}>
                  <div>
                    <span className="font-semibold text-sm">{otp.user_name}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${roleColors[otp.role] || ''}`}>{(otp.role || '').replace(/_/g, ' ')}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Username: <span className="font-mono font-medium">{otp.username}</span></p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl sm:text-3xl font-mono font-bold tracking-[0.3em]" style={{ color: 'hsl(43, 96%, 56%)' }}>{otp.otp}</span>
                    <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1"><Clock size={10} /> {formatDateTime(otp.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      {/* Mobile */}
      <div className="mobile-card-list">
        {users.map(u => (
          <Card key={u.id} data-testid={`user-mobile-card-${u.id}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1 mr-2">
                  <p className="font-semibold text-sm truncate">{u.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">@{u.username || '\u2014'}</p>
                  {u.mobile && <p className="text-xs text-muted-foreground">{u.mobile}</p>}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold mt-1 inline-block ${roleColors[u.role] || ''}`}>{(u.role || '').replace(/_/g, ' ')}</span>
                </div>
                {u.role === 'admin' ? (
                  <Badge>Admin</Badge>
                ) : (
                  <Switch checked={u.is_active} onCheckedChange={() => toggleUser(u.id)} />
                )}
              </div>
              {/* Mobile action buttons */}
              {u.role !== 'admin' && (
                <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-border/50">
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => openEdit(u)} data-testid={`user-mobile-edit-${u.id}`}>
                    <Pencil size={14} className="mr-1" />Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-destructive" onClick={() => handleDelete(u.id, u.name)} data-testid={`user-mobile-delete-${u.id}`}>
                    <Trash2 size={14} className="mr-1" />Delete
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop */}
      <div className="desktop-table">
        <Card>
          <CardContent className="p-0">
            <div className="table-scroll-wrapper">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="font-semibold">{u.name}</TableCell>
                      <TableCell className="font-mono text-sm">{u.username || '\u2014'}</TableCell>
                      <TableCell><span className={`px-2 py-1 rounded-full text-xs font-semibold ${roleColors[u.role] || ''}`}>{(u.role || '').replace(/_/g, ' ')}</span></TableCell>
                      <TableCell className="font-mono">{u.mobile || '\u2014'}</TableCell>
                      <TableCell className="text-sm">{formatDateTime(u.created_at)}</TableCell>
                      <TableCell>{u.role !== 'admin' ? <Switch checked={u.is_active} onCheckedChange={() => toggleUser(u.id)} /> : <Badge>Admin</Badge>}</TableCell>
                      <TableCell>
                        {u.role !== 'admin' && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil size={14} /></Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(u.id, u.name)} data-testid={`user-delete-${u.id}`}><Trash2 size={14} /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Full Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="user-name-input" /></div>
            <div><label className="text-sm font-medium mb-1 block">Username *</label><Input value={form.username} onChange={e => setForm({...form, username: e.target.value.replace(/\s/g, '')})} className="font-mono" required data-testid="user-username-input" /><p className="text-xs text-muted-foreground mt-1">Used for login. No spaces.</p></div>
            <div><label className="text-sm font-medium mb-1 block">Mobile</label><Input value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} maxLength={10} /></div>
            <div><label className="text-sm font-medium mb-1 block">Role *</label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="store_manager">Store Manager</SelectItem>
                  <SelectItem value="stock_manager">Stock Manager</SelectItem>
                  <SelectItem value="billing_executive">Billing Executive</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" data-testid="user-create-submit">Create User</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit User: {editUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Full Name</label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="user-edit-name" /></div>
            <div><label className="text-sm font-medium mb-1 block">Username</label><Input value={editForm.username} onChange={e => setEditForm({...editForm, username: e.target.value.replace(/\s/g, '')})} className="font-mono" data-testid="user-edit-username" /></div>
            <div><label className="text-sm font-medium mb-1 block">Mobile</label><Input value={editForm.mobile} onChange={e => setEditForm({...editForm, mobile: e.target.value})} maxLength={10} /></div>
            <div><label className="text-sm font-medium mb-1 block">Role</label>
              <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="store_manager">Store Manager</SelectItem>
                  <SelectItem value="stock_manager">Stock Manager</SelectItem>
                  <SelectItem value="billing_executive">Billing Executive</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleEdit} className="flex-1" data-testid="user-edit-save">Save Changes</Button>
              <Button variant="destructive" onClick={() => { handleDelete(editUser.id, editUser.name); setEditUser(null); }}>Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
